import { useState, useEffect } from 'react';
import axios from 'axios';
import { openProfile } from './CvNotes';

const API = 'https://carriera-backend.onrender.com/api';

// A candidate's past with Carriera: job sheets, Kate's screenings and the activity
// log. Shown in the candidate pop-up (pages/Candidates.js) and, so Steph does not
// have to leave her call list, in the History panel of each To Call row (2 Nov 2026).

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Heading({ children }) {
  return <p className="text-xs font-semibold text-carriera-muted uppercase tracking-widest mb-2">{children}</p>;
}

export function LinkedJobSheets({ candidateId, headers }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/job-sheets?archived=true&candidate_id=${candidateId}`, { headers })
      .then(({ data }) => setSheets(data.filter(s => s.candidate_id === candidateId)))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [candidateId]);

  if (loading) return <p className="text-xs text-carriera-muted">Loading...</p>;
  if (failed) return <p className="text-xs text-red-600">Could not load job sheets. Refresh to try again.</p>;
  if (!sheets.length) return <p className="text-xs text-carriera-muted">No job sheets yet.</p>;

  const statusColors = {
    profile_sent: 'bg-blue-50 text-blue-700',
    interview_scheduled: 'bg-amber-50 text-amber-700',
    placed: 'bg-green-50 text-green-700',
    rejected: 'bg-red-50 text-red-500'
  };

  return (
    <div className="space-y-2">
      {sheets.map(s => (
        <div key={s.id} className="flex items-center justify-between text-xs border border-carriera-border px-3 py-2">
          <div>
            <p className="font-medium text-carriera-text">{s.jobs?.title || '-'}</p>
            <p className="text-carriera-muted">{s.jobs?.clients?.company_name || '-'}</p>
          </div>
          <div className="text-right">
            <span className={`px-2 py-0.5 rounded text-xs ${statusColors[s.status] || ''}`}>
              {s.status?.replace(/_/g, ' ')}
            </span>
            {s.archived && <p className="text-carriera-muted mt-0.5">archived</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

const VERDICT_STYLE = {
  shortlist: ['Shortlist', 'bg-green-50 text-green-700'],
  maybe: ['Maybe', 'bg-amber-50 text-amber-700'],
  reject: ['Reject for this job', 'bg-red-50 text-red-500']
};

// excludeId leaves out the screening already shown (the To Call row itself).
export function Screenings({ candidateId, headers, excludeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/screenings?candidate_id=${candidateId}`, { headers })
      .then(({ data }) => setRows(data.filter(s => s.id !== excludeId)))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [candidateId, excludeId]);

  if (loading) return <p className="text-xs text-carriera-muted">Loading...</p>;
  if (failed) return <p className="text-xs text-red-600">Could not load Kate's screenings. Refresh to try again.</p>;
  if (!rows.length) {
    return <p className="text-xs text-carriera-muted">
      {excludeId ? 'No other screenings by Kate.' : 'Kate has not screened this candidate yet.'}
    </p>;
  }

  return (
    <div className="space-y-2">
      {rows.map(s => {
        const [label, color] = VERDICT_STYLE[s.verdict] || [s.verdict, ''];
        return (
          <div key={s.id} className="text-xs border border-carriera-border px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-carriera-text">
                {s.jobs ? `${s.jobs.title}${s.jobs.clients?.company_name ? ` · ${s.jobs.clients.company_name}` : ''}` : 'No specific job'}
              </p>
              <span className={`px-2 py-0.5 rounded flex-shrink-0 ${color}`}>{label}</span>
            </div>
            {s.reason && <p className="text-carriera-sec mt-1 whitespace-pre-wrap">{s.reason}</p>}
            {s.better_fit_for && <p className="text-carriera-muted mt-1">Better fit for: {s.better_fit_for}</p>}
            <p className="text-carriera-muted mt-1">
              Screened {formatDate(s.screened_at)}{s.source === 'pool' ? ' (pool match)' : ''}
              {s.call_result && `. Call: ${s.call_result.replace(/_/g, ' ')}, ${formatDate(s.called_at)}`}
              {s.kate_right === true && '. Kate right'}
              {s.kate_right === false && `. Kate wrong${s.wrong_reason ? ` (${s.wrong_reason.replace(/_/g, ' ')})` : ''}`}
            </p>
            {s.call_notes && <p className="text-carriera-sec mt-1 whitespace-pre-wrap">Notes: {s.call_notes}</p>}
            {s.profile_path && (
              <button onClick={() => openProfile(s.id)} className="text-carriera-accent hover:underline mt-1">
                Profile draft from Kate ({formatDate(s.profile_ready_at)}) →
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ActivityLog({ entries }) {
  if (!(entries || []).length) return <p className="text-xs text-carriera-muted">No activity recorded yet.</p>;
  return (
    <div className="space-y-0">
      {entries.map((entry, idx) => (
        <div key={entry.id} className="flex gap-3">
          <div className="flex flex-col items-center pt-1">
            <div className="w-2 h-2 rounded-full bg-carriera-accent flex-shrink-0" />
            {idx < entries.length - 1 && (
              <div className="flex-1 w-px bg-carriera-border mt-1" style={{ minHeight: 20 }} />
            )}
          </div>
          <div className="pb-4 flex-1">
            <p className="text-xs text-carriera-text leading-relaxed">{entry.details}</p>
            <p className="text-xs text-carriera-muted mt-0.5">{formatDate(entry.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// Everything above for one candidate, plus their notes. Loads only when opened.
export default function CandidateHistory({ candidateId, headers, excludeScreeningId }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/candidates/${candidateId}`, { headers })
      .then(({ data }) => setDetail(data))
      .catch(() => setError('Could not load the history.'));
  }, [candidateId]);

  return (
    <div className="space-y-4">
      <div>
        <Heading>Notes</Heading>
        {error ? <p className="text-xs text-red-600">{error}</p>
          : !detail ? <p className="text-xs text-carriera-muted">Loading...</p>
          : detail.notes ? <p className="text-xs text-carriera-sec leading-relaxed whitespace-pre-wrap">{detail.notes}</p>
          : <p className="text-xs text-carriera-muted">No notes.</p>}
      </div>
      <div>
        <Heading>Job sheets</Heading>
        <LinkedJobSheets candidateId={candidateId} headers={headers} />
      </div>
      <div>
        <Heading>Kate's other screenings</Heading>
        <Screenings candidateId={candidateId} headers={headers} excludeId={excludeScreeningId} />
      </div>
      <div>
        <Heading>Activity log</Heading>
        {detail ? <ActivityLog entries={detail.activity_log} /> : !error && <p className="text-xs text-carriera-muted">Loading...</p>}
      </div>
    </div>
  );
}
